import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../config";
import "./SOAVerifyPage.css";

function SOAVerifyPage() {
  const { token } = useParams();

  const [record, setRecord] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [agreement, setAgreement] = useState(false);

  const [isRep, setIsRep] = useState(false);
  const [repInfo, setRepInfo] = useState({});

  const [beneficiary, setBeneficiary] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const productOptions = {
    mapd: "Medicare Advantage Plans (Part C) and Cost Plans",
    pdp: "Stand-alone Medicare Prescription Drug Plans (Part D)",
    medsupp: "Medicare Supplement (Medigap) Products",
    dental: "Dental / Vision / Hearing Products",
    hospital: "Hospital Indemnity Products",
  };

  useEffect(() => {
    const loadSession = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/soa/session/${token}`);

        if (!res.ok) {
          setLoading(false);
          return;
        }

        const data = await res.json();

        setRecord(data.record || {});
        setProducts(data.products || []);

        const preselected = {};
        Object.keys(productOptions).forEach((key) => {
          preselected[key] = data.products?.includes(key);
        });

        setSelectedProducts(preselected);

        setBeneficiary({
          first_name: data.record?.first_name || "",
          last_name: data.record?.last_name || "",
          street_address: data.record?.street_address || "",
          city: data.record?.city || "",
          state: data.record?.state || "",
          zip_code: data.record?.zip_code || "",
          phone: data.record?.phone || "",
        });

        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    loadSession();
  }, [token]);

  const toggleProduct = (key) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = async () => {
    if (!agreement) {
      alert("You must agree before submitting.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/soa/sign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          beneficiary,
          selectedProducts,
          representative: isRep ? repInfo : null,
          agreement_confirmed: agreement,
        }),
      });

      if (!res.ok) {
        alert("Unable to submit SOA.");
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Error submitting form.");
    }
  };

  if (loading) {
    return <div className="soa-loading">Loading Scope of Appointment...</div>;
  }

  if (!record) {
    return (
      <div className="soa-loading">
        This Scope of Appointment link is invalid or expired.
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="soa-success">
        <h2>Thank You</h2>
        <p>Your Scope of Appointment has been successfully submitted.</p>
      </div>
    );
  }

  return (
    <div className="soa-container">
      <h1 className="soa-title">Scope of Appointment Confirmation Form</h1>

      <p className="soa-text">
        The Centers for Medicare and Medicaid Services requires agents to
        document the scope of a marketing appointment prior to any face-to-face
        sales meeting to ensure understanding of what will be discussed between
        the agent and the Medicare beneficiary, or their authorized
        representative. All information provided on this form is confidential and
        should be completed by each person with Medicare or their authorized
        representative.
      </p>

      <h3 className="soa-section">
        Please check the types of product(s) you want the agent to discuss.
      </h3>

      <div className="soa-products">
        {Object.keys(productOptions).map((key) => (
          <div key={key} className="soa-product-row">
            <input
              type="checkbox"
              checked={selectedProducts[key] || false}
              onChange={() => toggleProduct(key)}
            />

            <span>{productOptions[key]}</span>
          </div>
        ))}
      </div>

      <p className="soa-text">
        By signing this form, you agree to a meeting with a sales agent to
        discuss the types of products you indicated above. Please note, the
        person who will discuss the products is either employed or contracted by
        a Medicare plan. They do not work directly for the federal government.
        This individual may also be paid based on your enrollment in a plan.
        Signing this form does not obligate you to enroll in a plan, affect your
        current enrollment, or enroll you in a Medicare plan.
      </p>

      <h3 className="soa-section">Please fill out the following details:</h3>

      <p className="soa-note">
        Note: Each person enrolled in Medicare receives a Medicare Beneficiary
        Identifier number on their Medicare card. Enter the information of the
        primary Medicare beneficiary.
      </p>

      <div className="soa-grid">
        <div className="soa-field">
          <label>First Name *</label>
          <input
            value={beneficiary.first_name}
            onChange={(e) =>
              setBeneficiary({ ...beneficiary, first_name: e.target.value })
            }
          />
        </div>

        <div className="soa-field">
          <label>Last Name *</label>
          <input
            value={beneficiary.last_name}
            onChange={(e) =>
              setBeneficiary({ ...beneficiary, last_name: e.target.value })
            }
          />
        </div>

        <div className="soa-field">
          <label>Street Address *</label>
          <input
            value={beneficiary.street_address}
            onChange={(e) =>
              setBeneficiary({
                ...beneficiary,
                street_address: e.target.value,
              })
            }
          />
        </div>

        <div className="soa-field">
          <label>City</label>
          <input
            value={beneficiary.city}
            onChange={(e) =>
              setBeneficiary({ ...beneficiary, city: e.target.value })
            }
          />
        </div>

        <div className="soa-field">
          <label>State</label>
          <input
            value={beneficiary.state}
            onChange={(e) =>
              setBeneficiary({ ...beneficiary, state: e.target.value })
            }
          />
        </div>

        <div className="soa-field">
          <label>Zip Code</label>
          <input
            value={beneficiary.zip_code}
            onChange={(e) =>
              setBeneficiary({ ...beneficiary, zip_code: e.target.value })
            }
          />
        </div>

        <div className="soa-field">
          <label>Phone</label>
          <input
            value={beneficiary.phone}
            onChange={(e) =>
              setBeneficiary({ ...beneficiary, phone: e.target.value })
            }
          />
        </div>
      </div>

      <h3 className="soa-section">
        Are you the authorized representative acting on behalf of the
        beneficiary?
      </h3>

      <label className="soa-checkbox">
        <input
          type="checkbox"
          checked={isRep}
          onChange={() => setIsRep(!isRep)}
        />

        <span>Yes</span>
      </label>

      {isRep && (
        <div className="soa-grid">
          <div className="soa-field">
            <label>Authorized Representative First Name *</label>
            <input
              value={repInfo.first_name || ""}
              onChange={(e) =>
                setRepInfo({ ...repInfo, first_name: e.target.value })
              }
            />
          </div>

          <div className="soa-field">
            <label>Authorized Representative Last Name *</label>
            <input
              value={repInfo.last_name || ""}
              onChange={(e) =>
                setRepInfo({ ...repInfo, last_name: e.target.value })
              }
            />
          </div>

          <div className="soa-field">
            <label>Relationship to Beneficiary</label>
            <input
              value={repInfo.relationship || ""}
              onChange={(e) =>
                setRepInfo({ ...repInfo, relationship: e.target.value })
              }
            />
          </div>

          <div className="soa-field">
            <label>Phone</label>
            <input
              value={repInfo.phone || ""}
              onChange={(e) =>
                setRepInfo({ ...repInfo, phone: e.target.value })
              }
            />
          </div>
        </div>
      )}

      <div className="soa-agreement">
        <label className="soa-checkbox">
          <input
            type="checkbox"
            checked={agreement}
            onChange={() => setAgreement(!agreement)}
          />

          <span>
            By checking this box, I have read and understand the contents of the
            Scope of Appointment form, and I confirm that the information I have
            provided is accurate. If submitted by an authorized individual, this
            submission certifies that this person is authorized under State law
            to complete the Scope of Appointment form and that documentation of
            this authority is available upon request by Medicare.
          </span>
        </label>
      </div>

      <button className="soa-submit" onClick={handleSubmit}>
        Submit Scope of Appointment
      </button>

      <div className="soa-medicare-overview">
        <h3>Medicare Overview</h3>

        <p>
          Medicare Part C, or Medicare Advantage, combines the benefits of
          Medicare Parts A, B and typically D. Medicare Advantage plans typically
          come with additional benefits that Original Medicare doesn’t have and
          are distributed by private insurance companies. In many cases, you’ll
          need to use health care providers who participate in the plan’s network
          and service area for the lowest costs. These plans set a limit on what
          you’ll have to pay out-of-pocket each year for covered services, which
          could help protect you from unexpected costs.
        </p>

        <h4>Stand-alone Medicare Prescription Drug Plan (Part D)</h4>

        <p>
          Medicare Part D only helps pay for prescription drugs — following
          federal rules. Part D is often added to Original Medicare (Part A and
          B) or Medicare Advantage plans that are offered by private insurance
          companies. You pay a monthly plan premium for Part D coverage to the
          insurance provider. Since the coverage rules of Medicare Part D are
          regulated by the federal government, the prescription drugs it covers
          may vary from year to year.
        </p>

        <h4>Medicare Advantage Plans (Part C)</h4>

        <p>
          Medicare Part C, or Medicare Advantage, combines the benefits of
          Medicare Parts A, B and typically D. Medicare Advantage plans typically
          come with additional benefits that Original Medicare doesn’t have and
          are distributed by private insurance companies. In many cases, you’ll
          need to use health care providers who participate in the plan’s network
          and service area for the lowest costs. These plans set a limit on what
          you’ll have to pay out-of-pocket each year for covered services, which
          could help protect you from unexpected costs.
        </p>

        <p>Examples of Medicare Advantage Plans include:</p>

        <p>
          <strong>Medicare Health Maintenance Organization (HMO)</strong> –
          Provides all the benefits of Original Medicare and sometimes includes
          Part D. In most HMOs, you can only get your care from doctors or
          hospitals in the plan’s network (except in emergencies).
        </p>

        <p>
          <strong>Medicare Preferred Provider Organization (PPO) Plan</strong> –
          Provides all the benefits of Original Medicare and sometimes covers
          Part D. PPOs have network doctors and hospitals but you can also use
          out-of-network providers, typically at a higher cost.
        </p>

        <p>
          <strong>Medicare Private Fee-For-Services (PFFS) Plan</strong> – A
          plan that allows you to go to any Medicare-approved doctor, hospital
          and provider. The health provider must agree to treat you and accept
          the PFFS plan’s payment, terms and conditions. If your PFFS plan has a
          network, you can see any of the network providers who have agreed to
          treat members of the plan. You typically will pay more for
          out-of-network providers.
        </p>

        <p>
          <strong>Medicare Special Needs Plan (SNP)</strong> – A plan that has
          specific benefits intended for people with special health needs.
          Examples of special health needs include:
        </p>

        <ul>
          <li>People who qualify for both Medicare and Medicaid</li>
          <li>People in nursing homes</li>
          <li>People with approved chronic health conditions</li>
        </ul>

        <h4>Medicare Supplement (Medigap) Products</h4>

        <p>
          Medigap is Medicare Supplement Insurance that helps fill “gaps” in
          Original Medicare and is sold by private companies. A Medicare
          Supplement Insurance (Medigap) policy can help pay some of the
          remaining health care costs, like:
        </p>

        <ul>
          <li>Copayments</li>
          <li>Coinsurance</li>
          <li>Deductibles</li>
        </ul>

        <p>
          You must have Medicare Part A and Part B to get a Medicare Supplement
          plan. A Medigap plan is different from Medicare Part C because it only
          supplements your Original Medicare benefits. So, Medigap does not
          include Original Medicare or Part D coverage by itself.
        </p>

        <h4>Ancillary Products</h4>

        <p>
          You can get ancillary or secondary products through private insurance
          companies. These plans offer additional benefits to people who want
          separate coverage for things like dental, vision and hearing. Ancillary
          plans are not Medicare products.
        </p>
      </div>
    </div>
  );
}

export default SOAVerifyPage;